import React, { useState, useEffect } from 'react';
import { Dropdown, Badge, Button, List, Typography, Tag } from 'antd';
import { BellOutlined, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import NotificationService, { Notification } from '../../services/notifications';
import './NotificationDropdown.css';

const { Text } = Typography;

const NotificationDropdown: React.FC = () => {
  const navigate = useNavigate();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const unsubscribe = NotificationService.subscribe(setNotifications);
    NotificationService.connect();
    NotificationService.getUnreadCount()
      .then(setUnreadCount)
      .catch(() => setUnreadCount(0));
    return () => {
      unsubscribe();
      NotificationService.disconnect();
    };
  }, []);

  const mapNotificationType = (type: string): 'success' | 'warning' | 'error' | 'info' => {
    const normalized = type.toUpperCase();
    if (normalized.includes('REJECT')) return 'error';
    if (normalized.includes('APPROV') || normalized.includes('ACCEPT')) return 'success';
    if (normalized.includes('APPLIED') || normalized.includes('SUBMIT')) return 'info';
    return 'warning';
  };

  const getTimeAgo = (dateString: string): string => {
    if (!dateString) return 'Just now';
    const now = new Date();
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Just now';
    const diffInMs = now.getTime() - date.getTime();
    const diffInMins = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMins < 60) return `${diffInMins}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${diffInDays}d ago`;
  };

  const getStatusColor = (type: string, isRead: boolean): string => {
    if (isRead) return '#d9d9d9';
    switch (mapNotificationType(type)) {
      case 'success': return '#52c41a';
      case 'warning': return '#faad14';
      case 'error': return '#ff4d4f';
      default: return '#1890ff';
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    await NotificationService.markAsRead(notification.id);
    const count = await NotificationService.getUnreadCount().catch(() => 0);
    setUnreadCount(count);
    setDropdownVisible(false);
    navigate('/notifications');
  };

  const handleRemoveNotification = (id: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    NotificationService.removeNotification(id);
  };

  const handleMarkAsRead = async (id: number | string, e: React.MouseEvent) => {
    e.stopPropagation();
    await NotificationService.markAsRead(id);
    const count = await NotificationService.getUnreadCount().catch(() => 0);
    setUnreadCount(count);
  };

  const dropdownContent = (
    <div className="notification-dropdown">
      <div className="notification-header">
        <Text strong>Notifications</Text>
        <Button type="link" size="small" onClick={() => {
          setDropdownVisible(false);
          navigate('/notifications');
        }}>
          View All
        </Button>
      </div>
      <List
        dataSource={notifications}
        renderItem={(notification) => (
          <List.Item
            className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="notification-content">
              <div className="notification-main">
                <Text strong className="notification-title">{notification.title}</Text>
                <Text className="notification-message">{notification.message}</Text>
              </div>
              <div className="notification-meta">
                <Tag color={getStatusColor(notification.type, notification.isRead)}>
                  {getTimeAgo(notification.createdAt)}
                </Tag>
                {notification.isRead && (
                  <Tag color="#d9d9d9">Read</Tag>
                )}
                <Button
                  type="text"
                  size="small"
                  title="Mark as read"
                  icon={<CheckOutlined />}
                  onClick={(e) => handleMarkAsRead(notification.id, e)}
                  className="remove-btn"
                />
                <Button
                  type="text"
                  size="small"
                  title="Remove"
                  icon={<DeleteOutlined />}
                  onClick={(e) => handleRemoveNotification(notification.id, e)}
                  className="remove-btn"
                />
              </div>
            </div>
          </List.Item>
        )}
      />
      {notifications.length === 0 && (
        <div className="no-notifications">
          <Text type="secondary">No Notifications</Text>
        </div>
      )}
    </div>
  );

  const isMobileViewport =
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      placement={isMobileViewport ? 'bottom' : 'bottomRight'}
      overlayClassName="notification-dropdown-overlay"
      open={dropdownVisible}
      onOpenChange={setDropdownVisible}
      getPopupContainer={() => document.body}
      destroyPopupOnHide
    >
      <span className="notification-bell-wrap" role="presentation">
        <Badge count={unreadCount} size="small">
          <Button
            type="text"
            icon={<BellOutlined />}
            className="notification-bell"
            size="large"
            aria-label="Notifications"
          />
        </Badge>
      </span>
    </Dropdown>
  );
};

export default NotificationDropdown;